import { memo, useEffect, useRef } from 'react';
import { isMobile } from 'react-device-detect';
import moment from 'moment';
import { useMappedState } from 'redux-react-hook';
import styled from 'styled-components';

import TrackingAPI from '../../api/TrackingAPI';
import Modal from "../../ui-kit/Modal";
import { formatDateToFullFormatWithSeconds, FULL_DATE_FORMAT_WITH_SECONDS, now } from '../../utils/dates';
import ImageModalSection from './components/ImageModalSection';
import MainContentModalSection from './components/MainContentModalSection';

import './styles.css';

interface IStripeOfferBannerModalProps {
  isOpen: boolean;
  onClose: () => void;
  analyticsDataRenderReason?: string;
}

const StripeOfferBannerModal: React.FC<IStripeOfferBannerModalProps> = (props) => {
  const { isOpen, onClose, analyticsDataRenderReason = 'open_app' } = props;

  const { currentUser } = useMappedState(
    state => {
      const currentUserId = state.users.currentUserId;
      const currentUser = state.users.usersMap.get(currentUserId);
      return {
        currentUser
      };
    }
  );

  const subscriptionEndDate = formatDateToFullFormatWithSeconds(currentUser?.subscription?.currentPeriodEndDate ?? '');

  const expireSubscriptionDate = moment(subscriptionEndDate, FULL_DATE_FORMAT_WITH_SECONDS);
  const nowTime = now();
  const duration = moment.duration(expireSubscriptionDate.diff(nowTime));

  const daysToExpireSubscription = Math.floor(duration.asDays());
  const isExpireLessWeekSub = daysToExpireSubscription <= 6;
  const isExpiredSub = nowTime.isAfter(expireSubscriptionDate);

  const hasTrackedRef = useRef(false);

  useEffect(() => {
    const trackEvent = () => {
      if (hasTrackedRef.current) return;
      hasTrackedRef.current = true;

      const getAnalyticsDataByReasons = () => {
        return {
          rendered_for_reason: analyticsDataRenderReason,
          modal_type: isExpiredSub ? 'stripe_subs_is_expired' : 'stripe_subs_is_expiring_less_week',
        };
      };

      try {
        TrackingAPI.trackEvent('stripe_offer_banner_modal_rendered', getAnalyticsDataByReasons());
      } catch (error) {
        throw error;
      }
    };

    trackEvent();
  }, [analyticsDataRenderReason, isExpiredSub]);

  return (
    // @ts-ignore
    <Modal isOpen={isOpen} onClose={onClose} canClose={true} className="stripe-offer-banner-modal">
      <Wrapper>
        {!isMobile && <ImageModalSection isExpireLessWeekSub={isExpireLessWeekSub} isExpiredSub={isExpiredSub} />}
        <MainContentModalSection isExpiredSub={isExpiredSub} subscriptionEndDate={subscriptionEndDate} />
      </Wrapper>
    </Modal>
  );
};

export default memo(StripeOfferBannerModal);

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 65vh;
  width: 65vw;
  min-height: 600px;
  max-width: 880px;
  min-width: 800px;

  @media (max-width: 768px) {
    min-height: unset;
    min-width: unset;
    height: 90vh;
    width: 100%;
  }

  @media (max-height: 700px) {
    min-height: unset;
    min-width: unset;
    height: 96vh;
  }
`;
